import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AgreementButton from './AgreementButton';
import { useLang } from '../context/LangContext';

const AgreementSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AgreementContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin: 8px 0;
`;

const AgreementItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 4px 0;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: #666666;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #333333;
  }
`;

interface AgreeContent {
  title: string;
  onView?: () => void;
}

interface AgreementProps {
  allAgreeTitle?: string;
  agreeContents: AgreeContent[];
  onAgreementChange: (allAgreed: boolean, individualAgreements: boolean[]) => void;
  initialAgreements?: boolean[];
}

// 번역 객체
const translations = {
  allAgree: {
    ko: '전체 동의',
    en: 'Agree to All'
  },
  view: {
    ko: '보기',
    en: 'View'
  }
};

function Agreement({ 
  allAgreeTitle, 
  agreeContents, 
  onAgreementChange,
  initialAgreements = []
}: AgreementProps) {
  const { lang } = useLang();
  
  // allAgreeTitle이 제공되지 않으면 번역된 기본값 사용
  const defaultAllAgreeTitle = allAgreeTitle || translations.allAgree[lang];
  const [isAllAgreed, setIsAllAgreed] = useState(false);
  const [individualAgreements, setIndividualAgreements] = useState<boolean[]>(
    initialAgreements.length === agreeContents.length 
      ? initialAgreements 
      : new Array(agreeContents.length).fill(false)
  );

  // 개별 동의 상태가 변경될 때 전체 동의 상태 업데이트
  useEffect(() => {
    const allAgreed = individualAgreements.every(agreed => agreed);
    setIsAllAgreed(allAgreed);
    onAgreementChange(allAgreed, individualAgreements);
  }, [individualAgreements, onAgreementChange]);

  const handleAllAgreementChange = () => {
    const newValue = !isAllAgreed;
    setIsAllAgreed(newValue);
    const newIndividualAgreements = new Array(agreeContents.length).fill(newValue);
    setIndividualAgreements(newIndividualAgreements);
  };

  const handleIndividualAgreementChange = (index: number) => {
    const newIndividualAgreements = [...individualAgreements];
    newIndividualAgreements[index] = !newIndividualAgreements[index];
    setIndividualAgreements(newIndividualAgreements);
  };

  return (
    <AgreementSection>
      <AgreementContainer>
        <AgreementButton
          isClicked={isAllAgreed}
          onClicked={handleAllAgreementChange}
        >
          {defaultAllAgreeTitle}
        </AgreementButton>
      </AgreementContainer>

      {agreeContents.map((content, index) => (
        <AgreementItem key={index}>
          <AgreementButton
            isClicked={individualAgreements[index]}
            onClicked={() => handleIndividualAgreementChange(index)}
          >
            {content.title}
          </AgreementButton>
          {content.onView && (
            <ViewButton onClick={content.onView}>
              {translations.view[lang]}
            </ViewButton>
          )}
        </AgreementItem>
      ))}
    </AgreementSection>
  );
}

export default Agreement;
